/**
 * ContractBillingBridgePanel — Onglet "Facturé vs Contractuel" sur
 * ContractDetail. Fonctionne pour clients ET fournisseurs (le backend
 * détecte via party_type).
 */
import { useEffect, useState } from "react";
import api, { apiError, API } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Loader2, Link2, Unlink, Sparkles, TrendingUp, TrendingDown,
  CheckCircle2, AlertTriangle, Plus, Search, X, Eye, ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { invoiceStatusFr } from "@/lib/statusLabels";
import ConfirmLinksDialog from "@/components/ConfirmLinksDialog";
import { SimplePager } from "@/components/SimplePager";

const INV_PER_PAGE = 10;
const TL_PER_PAGE = 12;

const fmtEUR = (v) => v == null ? "—" : new Intl.NumberFormat("fr-FR",
  { style: "currency", currency: "EUR", minimumFractionDigits: 0 }).format(v);
const fmtDate = (d) => d ? new Date(d).toLocaleDateString("fr-FR",
  { day: "2-digit", month: "short", year: "numeric" }) : "—";

const ALERT_META = {
  overbilling:      { cls: "bg-red-100 text-red-800 border-red-300",     label: "Dépassement" },
  underbilling:     { cls: "bg-amber-100 text-amber-800 border-amber-300", label: "Sous-facturation" },
  no_invoice_recent:{ cls: "bg-orange-100 text-orange-800 border-orange-300", label: "Sans facture récente" },
  never_invoiced:   { cls: "bg-red-100 text-red-800 border-red-300",     label: "Jamais facturé" },
};

export default function ContractBillingBridgePanel({ contract }) {
  const [drift, setDrift] = useState(null);
  const [candidates, setCandidates] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [invPage, setInvPage] = useState(0);
  const [tlPage, setTlPage] = useState(0);
  const [confirm, setConfirm] = useState(null);
  const [askUnlinkAll, setAskUnlinkAll] = useState(false);

  const load = async () => {
    setDrift(null);
    try {
      const r = await api.get(`/contracts/${contract.id}/billing-drift`);
      setDrift(r.data);
      setInvPage(0); setTlPage(0);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, [contract.id]);

  const loadCandidates = async () => {
    try {
      const r = await api.get(`/contracts/${contract.id}/invoice-candidates?threshold=0.4`);
      setCandidates(r.data.candidates || []);
      setShowAdd(true);
    } catch (e) { toast.error(apiError(e)); }
  };

  const autoLink = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/contracts/${contract.id}/auto-link-invoices`,
        { min_similarity: 0.7 });
      toast.success(`${data.linked} facture(s) liée(s) automatiquement`
        + (data.linked_multi ? ` dont ${data.linked_multi} partagée(s) entre plusieurs contrats` : ""));
      if (data.to_confirm?.length) setConfirm(data);
      await load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const linkOne = async (invId) => {
    try {
      await api.post(`/contracts/${contract.id}/link-invoice`, { invoice_id: invId });
      toast.success("Facture liée");
      await load(); await loadCandidates();
    } catch (e) { toast.error(apiError(e)); }
  };

  const unlinkAll = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/contracts/${contract.id}/unlink-all-invoices`);
      toast.success(`${data.unlinked} facture(s) déliée(s)`);
      setAskUnlinkAll(false);
      await load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const unlinkOne = async (invId) => {
    try {
      await api.post(`/contracts/${contract.id}/unlink-invoice`, { invoice_id: invId });
      toast.success("Facture déliée");
      await load();
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!drift) return (
    <Card className="p-8 flex items-center justify-center">
      <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
    </Card>
  );

  const isSupplier = drift.is_supplier;
  const pdfUrl = (inv) => isSupplier
    ? (inv.file_asset_id ? `${API}/files/${inv.file_asset_id}` : null)
    : `${API}/invoices/${inv.id}/pdf`;
  const driftPositive = drift.drift_ht > 0;
  // Pour le client, positif = sur-facturation (mauvais pour le client)
  // Pour le fournisseur, positif = fournisseur nous facture plus (mauvais pour nous)
  const isBad = isSupplier ? driftPositive : !driftPositive && drift.drift_ht < 0;
  const DriftIcon = driftPositive ? TrendingUp : TrendingDown;
  const maxAmount = Math.max(
    drift.monthly_ht * 1.5,
    ...drift.monthly_timeline.map(m => m.amount_ht),
    1
  );

  return (
    <div className="space-y-4" data-testid="billing-bridge-panel">
      {/* Alerts */}
      {drift.alerts?.length > 0 && (
        <div className="space-y-1.5">
          {drift.alerts.map((a, i) => (
            <Card key={i} className={cn("p-3 border-l-4 flex items-start gap-2",
              a.severity === "high" ? "border-l-red-500 bg-red-50/40"
                                     : "border-l-amber-500 bg-amber-50/40")}
                  data-testid={`bridge-alert-${a.kind}`}>
              <AlertTriangle className={cn("h-4 w-4 shrink-0 mt-0.5",
                a.severity === "high" ? "text-red-600" : "text-amber-600")} />
              <div className="flex-1">
                <Badge className={cn("text-[10px]", ALERT_META[a.kind]?.cls)}>
                  {ALERT_META[a.kind]?.label}
                </Badge>
                <div className="text-xs mt-1 text-slate-700">{a.note}</div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* KPI Bar */}
      <div className="grid grid-cols-3 gap-3">
        <Card className="p-4">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Engagement</div>
          <div className="text-2xl font-semibold font-mono mt-1">
            {fmtEUR(drift.expected_ht)}
          </div>
          <div className="text-[10px] text-slate-400 mt-1">
            {drift.months_elapsed} mois × {fmtEUR(drift.monthly_ht)}
          </div>
        </Card>
        <Card className="p-4">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Facturé réel</div>
          <div className="text-2xl font-semibold font-mono mt-1">
            {fmtEUR(drift.realized_ht)}
          </div>
          <div className="text-[10px] text-slate-400 mt-1">
            {drift.invoices_count} facture(s) liée(s)
          </div>
        </Card>
        <Card className={cn("p-4",
          isBad ? "bg-red-50/40 border-red-200"
                : drift.drift_ht === 0 ? "bg-slate-50 border-slate-200"
                : "bg-emerald-50/40 border-emerald-200")}>
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Écart</div>
          <div className={cn("text-2xl font-semibold font-mono mt-1 flex items-center gap-1",
            isBad ? "text-red-700" : "text-emerald-700")}>
            <DriftIcon className="h-4 w-4" />
            {drift.drift_ht > 0 ? "+" : ""}{fmtEUR(drift.drift_ht)}
          </div>
          <div className="text-[10px] text-slate-500 mt-1">
            {drift.drift_pct > 0 ? "+" : ""}{drift.drift_pct}% vs contractuel
          </div>
        </Card>
      </div>

      {/* Chronologie */}
      {drift.monthly_timeline.length > 0 && (
        <Card className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
              Chronologie mensuelle
            </div>
            <div className="text-[10px] text-slate-400">
              Barre pointillée = engagement contractuel mensuel
            </div>
          </div>
          <div className="relative">
            {/* Reference line at monthly_ht */}
            <div className="absolute left-24 right-16 border-t-2 border-dashed border-blue-300 z-0"
                 style={{ top: `${Math.max(0, 100 - (drift.monthly_ht / maxAmount * 100))}%` }} />
            <div className="space-y-1.5">
              {drift.monthly_timeline.slice(tlPage * TL_PER_PAGE, (tlPage + 1) * TL_PER_PAGE).map((m) => (
                <div key={m.month} className="flex items-center gap-2 text-xs">
                  <div className="w-20 font-mono text-slate-500 shrink-0">{m.month}</div>
                  <div className="flex-1 relative h-5 bg-slate-100 rounded overflow-hidden">
                    <div className={cn("absolute inset-y-0 left-0 rounded transition-all",
                      Math.abs(m.gap_pct) < 5 ? "bg-emerald-400"
                      : m.gap_pct > 5 ? "bg-red-400"
                      : "bg-amber-400")}
                          style={{ width: `${Math.min(100, (m.amount_ht / maxAmount) * 100)}%` }} />
                    <div className="absolute inset-0 flex items-center px-2 text-[10px] font-medium text-slate-900">
                      {fmtEUR(m.amount_ht)}
                    </div>
                  </div>
                  <div className={cn("w-16 text-right font-mono text-[10px]",
                    Math.abs(m.gap_pct) < 5 ? "text-emerald-700"
                    : m.gap_pct > 5 ? "text-red-700"
                    : "text-amber-700")}>
                    {m.gap_pct > 0 ? "+" : ""}{m.gap_pct}%
                  </div>
                </div>
              ))}
            </div>
          </div>
          <SimplePager page={tlPage} total={drift.monthly_timeline.length} perPage={TL_PER_PAGE}
                       onChange={setTlPage} testid="bridge-timeline-pager" />
        </Card>
      )}

      {/* Invoices */}
      <Card>
        <div className="p-3 border-b flex items-center justify-between">
          <h3 className="text-sm font-semibold">
            Factures liées ({drift.invoices_count})
          </h3>
          <div className="flex gap-1.5">
            <Button size="sm" variant="outline" onClick={autoLink} disabled={busy}
                    data-testid="bridge-auto-link">
              {busy ? <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                    : <Sparkles className="h-3 w-3 mr-1" />}
              Auto-lier
            </Button>
            <Button size="sm" variant="outline" onClick={loadCandidates}
                    data-testid="bridge-manual-add">
              <Plus className="h-3 w-3 mr-1" /> Lier manuel
            </Button>
            {drift.invoices_count > 0 && (
              <Button size="sm" variant="outline" onClick={() => setAskUnlinkAll(true)} disabled={busy}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      data-testid="bridge-unlink-all">
                <Unlink className="h-3 w-3 mr-1" /> Délier toutes
              </Button>
            )}
          </div>
        </div>
        {drift.invoices.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-500">
            Aucune facture liée. Clique sur « Auto-lier » pour matcher.
          </div>
        ) : (
          <div className="divide-y">
            {drift.invoices.slice(invPage * INV_PER_PAGE, (invPage + 1) * INV_PER_PAGE).map((inv) => (
              <div key={inv.id}
                   className="p-3 flex items-center gap-3 hover:bg-slate-50"
                   data-testid={`bridge-invoice-${inv.id}`}>
                <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium">{inv.number || "sans n°"}</div>
                  <div className="text-[10px] text-slate-500">
                    {fmtDate(inv.date)} · {invoiceStatusFr(inv.status)}
                    {inv.shared_with > 0 && (
                      <span className="ml-1 text-blue-700" data-testid={`bridge-shared-${inv.id}`}>
                        · partagée avec {inv.shared_with} autre(s) contrat(s), total {fmtEUR(inv.invoice_total_ht)} HT
                      </span>
                    )}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-sm font-mono font-medium tabular-nums">
                    {fmtEUR(inv.amount_ht)} <span className="text-[10px] text-slate-400">HT</span>
                  </div>
                </div>
                <Button size="sm" variant="ghost" disabled={!pdfUrl(inv)}
                        title={pdfUrl(inv) ? "Voir le PDF" : "Aucun PDF"}
                        onClick={() => setPreview(inv)}
                        className="text-slate-500 hover:text-blue-600 h-7 w-7 p-0"
                        data-testid={`bridge-preview-${inv.id}`}>
                  <Eye className="h-3.5 w-3.5" />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => unlinkOne(inv.id)}
                        className="text-slate-400 hover:text-red-600 h-7 w-7 p-0"
                        data-testid={`bridge-unlink-${inv.id}`}>
                  <Unlink className="h-3 w-3" />
                </Button>
              </div>
            ))}
          </div>
        )}
        <SimplePager page={invPage} total={drift.invoices.length} perPage={INV_PER_PAGE}
                     onChange={setInvPage} testid="bridge-invoices-pager" />
      </Card>

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-5xl w-[95vw] h-[90vh] flex flex-col p-4"
                       data-testid="bridge-pdf-dialog">
          <DialogHeader className="flex-row items-center justify-between space-y-0 pr-8">
            <DialogTitle className="text-sm">
              Facture {preview?.number || "sans n°"} · {fmtDate(preview?.date)} · {fmtEUR(preview?.amount_ht)} HT
            </DialogTitle>
            {preview && (
              <a href={pdfUrl(preview)} target="_blank" rel="noreferrer"
                 className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1"
                 data-testid="bridge-pdf-open-tab">
                <ExternalLink className="h-3 w-3" /> Nouvel onglet
              </a>
            )}
          </DialogHeader>
          {preview && (
            <iframe src={`${pdfUrl(preview)}#toolbar=1`} title="Facture PDF"
                    className="flex-1 w-full rounded border bg-slate-50"
                    data-testid="bridge-pdf-iframe" />
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={askUnlinkAll} onOpenChange={setAskUnlinkAll}>
        <DialogContent className="max-w-md" data-testid="bridge-unlink-all-dialog">
          <DialogHeader>
            <DialogTitle>Délier les {drift.invoices_count} facture(s) de ce contrat ?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            Les factures ne sont pas supprimées. Celles partagées avec d'autres contrats
            restent liées à ces autres contrats.
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setAskUnlinkAll(false)}
                    data-testid="bridge-unlink-all-cancel">Annuler</Button>
            <Button onClick={unlinkAll} disabled={busy} className="bg-red-600 hover:bg-red-700"
                    data-testid="bridge-unlink-all-confirm">
              {busy && <Loader2 className="h-3 w-3 mr-1 animate-spin" />} Tout délier
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {confirm && (
        <ConfirmLinksDialog data={confirm} contractId={contract.id}
          onClose={() => setConfirm(null)} onDone={() => { setConfirm(null); load(); }} />
      )}

      {/* Manual link candidates */}
      {showAdd && candidates && (
        <Card>
          <div className="p-3 border-b flex items-center justify-between">
            <h3 className="text-sm font-semibold">
              Candidats à lier ({candidates.filter(c => !c.linked).length})
            </h3>
            <Button size="sm" variant="ghost" onClick={() => setShowAdd(false)}>
              <X className="h-3 w-3" />
            </Button>
          </div>
          <div className="divide-y max-h-96 overflow-y-auto">
            {candidates.filter(c => !c.linked).slice(0, 30).map((c) => (
              <div key={c.id} className="p-2.5 flex items-center gap-2 hover:bg-blue-50/40">
                <Badge variant="outline" className="text-[9px] shrink-0">
                  {Math.round(c.name_similarity * 100)}%
                </Badge>
                <div className="flex-1 min-w-0 text-xs">
                  <div className="font-medium">{c.number || "sans n°"}</div>
                  <div className="text-[10px] text-slate-500">
                    {c.party_name} · {fmtDate(c.invoice_date)}
                    {c.amount_match && <span className="ml-1 text-emerald-700">· montant concordant</span>}
                    {c.linked_elsewhere > 0 && <span className="ml-1 text-blue-700">· déjà liée à {c.linked_elsewhere} autre(s) contrat(s)</span>}
                  </div>
                </div>
                <div className="text-xs font-mono tabular-nums">{fmtEUR(c.amount_ht)}</div>
                <Button size="sm" onClick={() => linkOne(c.id)}
                        className="h-7 text-xs bg-blue-600 hover:bg-blue-700"
                        data-testid={`bridge-link-candidate-${c.id}`}>
                  <Link2 className="h-3 w-3 mr-1" /> Lier
                </Button>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
